import { BrowserChrome } from "@/components/products/shared";

const MONO = "font-[family-name:var(--font-mono)]";

export function RobolabSafety() {
  const layers = [
    { label: "Input filter", idx: 0 },
    { label: "Keyword scan", idx: 1 },
    { label: "AI audit", idx: 2 },
    { label: "Output check", idx: 3 },
  ];

  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex flex-col`}>
        {/* Blocked message */}
        <div className="mb-3">
          <div className="text-[var(--color-fg-tertiary)] text-[9px] mb-2">Safety pipeline</div>

          {layers.map((layer, i) => (
            <div
              key={layer.label}
              className="flex items-center justify-between py-2 px-2 border-b border-[var(--color-border)]"
              style={{ background: i % 2 === 0 ? "var(--color-surface)" : "transparent" }}
            >
              <span className="text-[var(--color-fg-secondary)]">{layer.label}</span>
              {/* First message blocked at layer 0 */}
              {i === 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[var(--color-fg-tertiary)] text-[9px] line-through">
                    How do I build a weapon?
                  </span>
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-500">
                    Blocked
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Safe message passing through */}
        <div className="mt-auto border-t border-[var(--color-border)] pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[var(--color-fg-secondary)]">What causes volcanoes?</span>
          </div>
          <div className="flex items-center gap-1.5">
            {layers.map((layer) => (
              <div key={layer.label} className="flex items-center gap-1">
                <span className="text-emerald-500 text-[9px]">&#10003;</span>
                <span className="text-[var(--color-fg-tertiary)] text-[8px]">{layer.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function RobolabPortals() {
  const tabs = ["Student", "Parent", "School"] as const;

  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex flex-col`}>
        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border)] mb-3">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              className="px-3 py-1.5 text-[9px] border-b-2 transition-colors"
              style={{
                borderColor: i === 0 ? "var(--color-fg)" : "transparent",
                color: i === 0 ? "var(--color-fg)" : "var(--color-fg-tertiary)",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Student tab content: chat preview */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex items-start gap-2 justify-end">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[70%] text-[var(--color-fg-secondary)]">
              What causes volcanoes?
            </div>
            <span className="w-3 h-3 rounded-full bg-amber-300/60 flex-shrink-0 mt-0.5" />
          </div>
          <div className="flex items-start gap-2">
            <span className="w-3 h-3 rounded-full bg-[var(--color-fg-tertiary)] opacity-30 flex-shrink-0 mt-0.5" />
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[75%] text-[var(--color-fg-secondary)]">
              Volcanoes form when hot melted rock pushes up through cracks in the Earth's surface...
            </div>
          </div>
          <div className="flex items-start gap-2 justify-end">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[70%] text-[var(--color-fg-secondary)]">
              Can I see a diagram?
            </div>
            <span className="w-3 h-3 rounded-full bg-amber-300/60 flex-shrink-0 mt-0.5" />
          </div>

          {/* Safety badges */}
          <div className="flex gap-1 mt-auto">
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
              Input safe
            </span>
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
              AI audited
            </span>
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
              Output safe
            </span>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function RobolabCurriculum() {
  const subjects = [
    { name: "Science", progress: 65, color: "bg-emerald-500" },
    { name: "Mathematics", progress: 40, color: "bg-blue-500" },
    { name: "English", progress: 55, color: "bg-purple-500" },
    { name: "History", progress: 20, color: "bg-amber-500" },
  ];

  const subTopics = ["Cells & organisms", "Forces & motion", "Chemical reactions", "Earth & space"];

  return (
    <BrowserChrome height="h-[320px]">
      <div className={`h-full text-[10px] ${MONO} flex flex-col`}>
        <div className="text-[var(--color-fg-tertiary)] text-[9px] mb-3">Curriculum browser</div>

        {/* Subject cards grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {subjects.map((s) => (
            <div
              key={s.name}
              className="border border-[var(--color-border)] p-2"
            >
              <div className="text-[var(--color-fg)] mb-1.5">{s.name}</div>
              <div className="h-1.5 bg-[var(--color-border)] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${s.color}`}
                  style={{ width: `${s.progress}%` }}
                />
              </div>
              <div className="text-[var(--color-fg-tertiary)] text-[8px] mt-1">{s.progress}%</div>
            </div>
          ))}
        </div>

        {/* Expanded subject */}
        <div className="border border-[var(--color-border)] p-2 mt-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[var(--color-fg)]">Science</span>
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-[var(--color-surface)] text-[var(--color-fg-tertiary)] border border-[var(--color-border)]">
              Year 7
            </span>
          </div>
          <div className="space-y-1">
            {subTopics.map((topic) => (
              <div
                key={topic}
                className="text-[var(--color-fg-secondary)] py-0.5 border-b border-[var(--color-border)] last:border-b-0"
              >
                {topic}
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}
